import { FC, useEffect, useMemo, useState } from "react";
import styles from "./styles.module.scss";
import { CrossIcon, ExternalLinkIcon } from "../../assets";
import { IUser } from "../../models";
import StreakConsistency from "../StreakConsistency";
import dayjs from "dayjs";
import UserInfoCoachView from "../UserInfoCoachView";
import UserActionTracker from "../UserActionTracker";
import UserDailyDeficit from "../UserDailyDeficit";
import UserFatLoss from "../UserFatLoss";
import { getMealIntervalLogs, getUserById } from "../../services/users.service";
import { toast } from "react-hot-toast";
import { PulseLoader } from "react-spinners";
import {
  ActivitySection,
  Button,
  EstimateCard,
  LoggedContainer,
  MealSection,
  WeightSection,
} from "../../components";
import DateRange from "../DateRange";
import WeeklyDays from "../WeekDays";
import { useQuery } from "@tanstack/react-query";
import CalorieGoal from "../CalorieGoal";
import { CalorieCharts, ProgressDateRange } from "..";
import WeeklyDeficit from "../WeeklyDeficit";
import CalorieBurn from "../CalorieBurn";

import { motion } from "framer-motion";

type Props = {
  isVisible: boolean;
  onClose: () => void;
  refetchUsers: () => void;
  userId: number | string;
};

const UserProfileCoachView: FC<Props> = ({
  isVisible,
  onClose,
  userId,
  refetchUsers,
}): JSX.Element | null => {
  const [progressDate, setProgressDate] = useState(dayjs().utc(true));
  const [activeDate, setActiveDate] = useState(dayjs().utc(true));

  const [selectedChart, setSelectedChart] = useState<
    "Calories IN" | "Calories OUT" | "Calories Deficit"
  >("Calories Deficit");
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [activeTab, setActiveTab] = useState<
    "Meal Plan" | "Progress" | "Weekly Stats"
  >("Progress");

  const fromDate = activeDate.startOf("isoWeek").format("YYYY-MM-DD");
  const toDate = activeDate.endOf("isoWeek").format("YYYY-MM-DD");

  const { data: mealInterval, isPending } = useQuery({
    queryKey: ["meal-interval-log", userId, fromDate, toDate],
    queryFn: () => {
      return getMealIntervalLogs(userId, fromDate, toDate);
    },
    enabled: !!userId && isVisible,
  });

  const currentMealLog = useMemo(() => {
    return mealInterval?.find((log) => {
      return log.date === activeDate.toISOString().split("T")[0];
    });
  }, [mealInterval, activeDate]);

  const [userProfile, setUserProfile] = useState<IUser>();

  const onGetUserProfile = async (id: string) => {
    try {
      setIsLoadingProfile(true);
      const { data } = await getUserById(id);
      if (data?.data) {
        setUserProfile(data?.data);
      }
    } catch (error) {
      toast.error(error?.message?.[0]?.message || "Something went wrong");
    } finally {
      setIsLoadingProfile(false);
    }
  };

  useEffect(() => {
    if (userId && isVisible) {
      onGetUserProfile(typeof userId === "number" ? userId.toString() : userId);
    }
  }, [userId, isVisible]);

  const isDaySkipped =
    currentMealLog?.completionType === "Skip" ||
    currentMealLog?.completionType === "Auto-Skip" ||
    currentMealLog?.completionType === "Average";

  if (isVisible) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        className={styles.overlay}
      >
        <motion.div
          initial={{ x: +100 }}
          animate={{ x: 0 }}
          className={styles.wrapper}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <CrossIcon
            className={styles.crossIcon}
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
          />
          <ExternalLinkIcon
            className={styles.externalLinkIcon}
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/users/${userId}`, "_blank");
            }}
          />
          {isLoadingProfile || !userProfile ? (
            <div className={styles.loaderWrapper}>
              <PulseLoader color={"#7D4AFB"} />
            </div>
          ) : (
            <>
              <UserInfoCoachView
                user={userProfile}
                refetchUser={() =>
                  onGetUserProfile(
                    typeof userId === "number" ? userId.toString() : userId,
                  )
                }
                refetchUsers={refetchUsers}
              />
              <div className={styles.sectionDivider} />

              <UserActionTracker
                user={userProfile}
                refetchUser={() =>
                  onGetUserProfile(
                    typeof userId === "number" ? userId.toString() : userId,
                  )
                }
              />
              <div className={styles.sectionDivider} />
              <UserDailyDeficit
                user={userProfile}
                refetchUser={() =>
                  onGetUserProfile(
                    typeof userId === "number" ? userId.toString() : userId,
                  )
                }
              />
              <div className={styles.sectionDivider} />
              <UserFatLoss user={userProfile} />
              <div className={styles.sectionDivider} />
              <div className={styles.tabsContainer}>
                <Button
                  onClick={() => setActiveTab("Progress")}
                  size="x-small"
                  title="Progress"
                  styleType={activeTab === "Progress" ? "filled" : "inactive"}
                />
                <Button
                  onClick={() => setActiveTab("Meal Plan")}
                  size="x-small"
                  title="Meal Plan"
                  styleType={activeTab === "Meal Plan" ? "filled" : "inactive"}
                />

                <Button
                  onClick={() => setActiveTab("Weekly Stats")}
                  size="x-small"
                  title="Weekly Stats"
                  styleType={
                    activeTab === "Weekly Stats" ? "filled" : "inactive"
                  }
                />
              </div>
              {activeTab === "Progress" ? (
                <>
                  <ProgressDateRange
                    date={progressDate}
                    setDate={setProgressDate}
                    user={userProfile}
                  />
                  <CalorieCharts date={progressDate} user={userProfile} />
                  <StreakConsistency
                    date={progressDate}
                    user={userProfile}
                    className={styles.streakCalendar}
                  />
                </>
              ) : activeTab === "Meal Plan" ? (
                <>
                  <DateRange
                    date={activeDate}
                    setDate={setActiveDate}
                    user={userProfile}
                  />

                  <div className={styles.mealPlanContainer}>
                    <WeeklyDays
                      date={activeDate}
                      setDate={setActiveDate}
                      user={userProfile}
                      mealInterval={mealInterval}
                    />
                    <CalorieGoal
                      user={userProfile}
                      mealLog={currentMealLog}
                      selectedWeekMealInterval={mealInterval}
                    />
                    {currentMealLog?.hasCustomCalories ? (
                      <LoggedContainer order={"single"} status={"logged"}>
                        <EstimateCard
                          calories={currentMealLog?.loggedValues?.calories}
                        />
                      </LoggedContainer>
                    ) : null}
                    <div className={styles.sectionsContainer}>
                      {currentMealLog?.sections?.map((section) => {
                        return (
                          <MealSection
                            section={section}
                            key={section.logSectionId}
                            isSkipped={isDaySkipped}
                          />
                        );
                      })}
                    </div>
                    {currentMealLog?.activities?.length ? (
                      <ActivitySection
                        activities={currentMealLog?.activities}
                        isSkipped={isDaySkipped}
                      />
                    ) : null}
                    {currentMealLog?.activities?.length ? (
                      <ActivitySection
                        activities={currentMealLog?.activities}
                        isSkipped={isDaySkipped}
                      />
                    ) : null}
                    {currentMealLog?.isLoggedWeight ? (
                      <WeightSection
                        weight={currentMealLog?.weight}
                        weightUnit={userProfile?.weight?.unit}
                        isSkipped={isDaySkipped}
                      />
                    ) : null}
                  </div>
                </>
              ) : (
                <div className={styles.weeklyStatsContainer}>
                  <DateRange
                    date={activeDate}
                    setDate={setActiveDate}
                    user={userProfile}
                  />
                  <WeeklyDeficit selectedWeekMealInterval={mealInterval} />
                  <CalorieBurn
                    date={activeDate}
                    user={userProfile}
                    selectedWeekMealInterval={mealInterval}
                  />
                </div>
              )}
            </>
          )}
        </motion.div>
      </motion.div>
    );
  }

  return null;
};

export default UserProfileCoachView;
